import React, { useMemo, useState } from "react";
import __ from "src/utils/Translations";
import useIsAhr from "src/utils/hooks/useIsAhr";
import { useCompanyId, useCompanyName } from "src/Pages/Company/CompanyContext";
import { getCompanyBaseBreadcrumbs } from "src/Pages/Company/routerHelper";
import BreadcrumbsMui from "src/Components/BreadcrumbsMui/BreadcrumbsMui";
import ImportExportLayout from "src/Pages/Company/OrganizationStructure/Layouts/ImportExportLayout";
import BackButton from "src/Pages/Company/OrganizationStructure/sharedComponents/BackButton";
import { Button } from "@mui/material";
import DownloadHistory, {
  ImportEntry,
} from "src/Pages/Company/OrganizationStructure/sharedComponents/DownloadHistory/DownloadHistory";
import { useFeatureIsOn } from "@growthbook/growthbook-react";
import FEATURE_FLAGS from "src/utils/constants/featureFlags";
import { usePermission } from "src/utils/hooks/permissions/usePermissions";
import {
  organizationStructureLocalizationsExport,
  organizationStructureLocalizationsImport,
  organizationStructureLocalizationsImportHistory,
} from "src/utils/RoleBasedSecurity/permissions";
import {
  handleFileDownload,
  ExportContentType,
} from "src/Pages/Company/OrganizationStructure/utils/handleFileDownload";
import { useFetchStructureData } from "src/Pages/Company/EmployeeManagement/CompanyEmployees/Edit/OrganizationStructure/api/useFetchStructureData";
import {
  downloadLocalizationsData,
  importLocalizationData,
} from "src/Pages/Company/OrganizationStructure/api";
import useImportData from "src/Pages/Company/OrganizationStructure/api/useImportData";
import useImportModal from "src/Pages/Company/OrganizationStructure/sharedComponents/useImportModal";
import {
  getLocalizationsUpdateFields,
  getLocalizationsImportInstructions,
} from "./constants";

const ImportExportLocalizations = () => {
  const companyId = useCompanyId();
  const isAhr = useIsAhr();
  const companyName = useCompanyName();
  const { data: structureData } = useFetchStructureData(companyId);
  const structureId: string = structureData?.[0]?.structureId ?? "";

  const [processData, setProcessData] = useState<{ id: string } | undefined>(
    undefined
  );

  const { mutate: importData } = useImportData({
    mutationFn: (file: File) =>
      importLocalizationData({ companyId, structureId, file }),
    successMessage: __("Pomyślnie zaimportowano lokalizacje"),
    errorMessage: __("Nie udało się zaimportować lokalizacji"),
    onImportSuccess: (data) => setProcessData(data),
  });

  const { open: openImportModal } = useImportModal({
    title: __("Import danych lokalizacji"),
    mutates: [importData],
  });

  const hasExportLocalizationsEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_LOCALIZATIONS_EXPORT
  );
  const hasImportLocalizationsEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_LOCALIZATIONS_IMPORT
  );
  const hasImportExportLocalizationsHistoryEnabled = useFeatureIsOn(
    FEATURE_FLAGS.ORGANIZATION_STRUCTURE_LOCALIZATIONS_IMPORT_EXPORT_HISTORY
  );
  const canImportLocalizations = usePermission(
    organizationStructureLocalizationsImport
  );
  const canExportLocalizations = usePermission(
    organizationStructureLocalizationsExport
  );
  const canPreviewImportHistory = usePermission(
    organizationStructureLocalizationsImportHistory
  );

  const baseBreadcrumbs = (
    getCompanyBaseBreadcrumbs(companyId, companyName) as {
      title: string;
      link: string;
    }[]
  ).map(({ title, link }) => ({
    label: title,
    to: link,
  }));

  const instructions = getLocalizationsImportInstructions();
  const chipLabels = getLocalizationsUpdateFields();

  const breadcrumbs = isAhr
    ? [
        {
          label: `${__("Zarządzanie firmą")} ${companyName}`,
          to: `/ahr`,
        },
        {
          label: __("Struktura organizacji"),
          to: `/ahr/organization-structure`,
        },
        {
          label: __("Lokalizacje"),
          to: `/ahr/organization-structure/localizations`,
        },
        {
          label: __("Import export lokalizacji"),
          to: `/ahr/organization-structure/localizations/import-export-localizations`,
        },
      ]
    : [
        ...baseBreadcrumbs,
        {
          label: __("Struktura organizacji"),
          to: `/company/edit/${companyId}/organization-structure`,
        },
        {
          label: __("Lokalizacje"),
          to: `/company/edit/${companyId}/organization-structure/localizations`,
        },
        {
          label: __("Import export lokalizacji"),
          to: `/company/edit/${companyId}/organization-structure/localizations/import-export-localizations`,
        },
      ];

  const imports = useMemo<ImportEntry[]>(
    () => (processData ? [{ processData }] : []),
    [processData]
  );

  return (
    <>
      <BreadcrumbsMui items={breadcrumbs} />
      <BackButton sx={{ mt: 4, mb: 3, p: 0 }} />
      <ImportExportLayout>
        <ImportExportLayout.Header
          title={__("Eksport i import lokalizacji")}
          description={__(
            "Na tej stronie możesz eksportować lokalizacje, wprowadzić w nich zmiany w pliku Excel, a następnie zaimportować zaktualizowane informacje z powrotem do systemu."
          )}
        />
        {hasExportLocalizationsEnabled && canExportLocalizations && (
          <>
            <ImportExportLayout.SectionHeader
              title={__("Eksport lokalizacji")}
            />
            <ImportExportLayout.ActionCard
              title={__("Eksportuj do pliku")}
              description={__("Eksportuj do pliku dane dotyczące lokalizacji")}
              actions={
                <>
                  <Button
                    variant="outlined"
                    data-testid="os__localizations-export-csv-button"
                    onClick={() =>
                      handleFileDownload(() =>
                        downloadLocalizationsData({
                          companyId,
                          structureId,
                          contentType: ExportContentType.CSV,
                        })
                      )
                    }
                  >
                    {__("Eksportuj plik CSV")}
                  </Button>
                  <Button
                    variant="outlined"
                    data-testid="os__localizations-export-xlsx-button"
                    onClick={() =>
                      handleFileDownload(() =>
                        downloadLocalizationsData({
                          companyId,
                          structureId,
                          contentType: ExportContentType.XLSX,
                        })
                      )
                    }
                  >
                    {__("Eksportuj plik XLSX")}
                  </Button>
                </>
              }
            />
            <ImportExportLayout.Divider />
          </>
        )}
        <ImportExportLayout.SectionHeader title={__("Szablon do importów")} />
        <ImportExportLayout.ActionCard
          title={__("Pobierz szablon do importów")}
          description={__(
            "Pobrany szablon możesz wykorzystać do przygotowania importu lokalizacji"
          )}
          actions={
            <>
              <Button
                variant="outlined"
                data-testid="os__localizations-template-csv-button"
                onClick={() =>
                  handleFileDownload(() =>
                    downloadLocalizationsData({
                      companyId,
                      structureId,
                      contentType: ExportContentType.CSV,
                      template: true,
                    })
                  )
                }
              >
                {__("Pobierz szablon CSV")}
              </Button>
              <Button
                variant="outlined"
                data-testid="os__localizations-template-xlsx-button"
                onClick={() =>
                  handleFileDownload(() =>
                    downloadLocalizationsData({
                      companyId,
                      structureId,
                      contentType: ExportContentType.XLSX,
                      template: true,
                    })
                  )
                }
              >
                {__("Pobierz szablon XLSX")}
              </Button>
            </>
          }
        />
        <ImportExportLayout.Divider />
        {hasImportLocalizationsEnabled && canImportLocalizations && (
          <>
            <ImportExportLayout.SectionHeader
              title={__("Import lokalizacji")}
            />
            <ImportExportLayout.Instructions instructions={instructions} />
            <ImportExportLayout.ActionCard
              title={__("Importuj lokalizacje")}
              description={__("Wgraj plik .csv lub .xlsx")}
              actions={
                <Button
                  variant="outlined"
                  data-testid="os__localizations-import-button"
                  onClick={openImportModal}
                >
                  {__("Importuj")}
                </Button>
              }
              metadata={{
                label: __("Dane aktualizowane podczas tego importu"),
                chips: chipLabels,
              }}
            />
            {hasImportExportLocalizationsHistoryEnabled &&
              canPreviewImportHistory && <DownloadHistory imports={imports} />}
          </>
        )}
      </ImportExportLayout>
    </>
  );
};

export default ImportExportLocalizations;
